import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Cell, CellProps, Column, useTable } from 'react-table'

import Box from '@mui/material/Box'
import Chip, { ChipProps } from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

import HistoryIcon from '@mui/icons-material/History'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'

import Pagination from '@/components/ui/pagination/Pagination'
import TextInput from '@/components/ui/inputs/TextInput'

import { countAvailableDoctors, useRoomDoctorStatuses } from '@/hooks/useRoomDoctorStatuses'

type WaitingRoomData = {
  id: string
  doctorIds: string[]
  isOpening: boolean
  name: string
  avgWaitingTime: number
}

type SelectWaitingRoomStepProps = {
  data: WaitingRoomData[]
  favoriteData?: WaitingRoomData[]
  lastSelectedRoom?: WaitingRoomData | null
  isFavorite?: (id: string) => boolean
  loading?: boolean
  onPageChange?: (newPage: number, rowsPerPage: number) => void
  onSelect?: (waitingRoomData: WaitingRoomData) => void
  onTextSearch?: (text: string) => void
  onToggleFavorite?: (id: string) => void
  totalCount: number
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared row renderer
// ─────────────────────────────────────────────────────────────────────────────

function WaitingRoomRow({
  room,
  isSelected,
  isFav,
  onSelect,
  onToggleFavorite,
  t,
}: {
  room: WaitingRoomData
  isSelected: boolean
  isFav: boolean
  onSelect: () => void
  onToggleFavorite?: () => void
  t: TFunction
}) {
  // Live (online + idle) availability — never registered-members minus occupied.
  const { statuses, isReady } = useRoomDoctorStatuses(room.id)
  const availableDoctors = countAvailableDoctors(room.doctorIds, statuses)

  return (
    <Paper
      component={TableRow}
      onClick={room.isOpening ? onSelect : undefined}
      selected={isSelected}
      sx={[
        { borderRadius: '6px' },
        room.isOpening && { cursor: 'pointer' },
      ]}
    >
      <TableCell sx={{ borderBottom: 'none', p: { xs: '8px', sm: '16px' }, borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px', width: 40 }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onToggleFavorite?.()
          }}
          sx={{ color: isFav ? 'warning.main' : 'action.disabled' }}
        >
          {isFav ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </IconButton>
      </TableCell>
      <TableCell sx={{ borderBottom: 'none', p: { xs: '8px', sm: '16px' } }}>
        <Typography variant="body2">{room.name}</Typography>
      </TableCell>
      <TableCell sx={{ borderBottom: 'none', p: { xs: '8px', sm: '16px' } }} align="center">
        <Chip
          color={room.isOpening ? 'success' : 'error'}
          label={t(`common:model.waitingRoom.status.${room.isOpening ? 'open' : 'closed'}.label`)}
          size="small"
          variant="filled"
          sx={{ fontSize: '12px' }}
        />
      </TableCell>
      <TableCell sx={{ borderBottom: 'none', p: { xs: '8px', sm: '16px' } }} align="center">
        <Chip
          color="info"
          label={t('common:model.waitingRoom.avgWaitingTime.label', { avgWaitingTime: room.avgWaitingTime })}
          size="small"
          variant="filled"
          sx={{ fontSize: '12px' }}
        />
      </TableCell>
      <TableCell sx={{ borderBottom: 'none', p: { xs: '8px', sm: '16px' } }}>
        {isReady
          ? t('page.createConsultation.step.waitingRoomSelection.availableDoctors', {
              count: availableDoctors,
              context: `${availableDoctors}`,
            })
          : '—'}
      </TableCell>
      <TableCell sx={{ borderBottom: 'none', p: { xs: '8px', sm: '16px' }, borderTopRightRadius: '6px', borderBottomRightRadius: '6px' }} />
    </Paper>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export function SelectWaitingRoomStep(props: SelectWaitingRoomStepProps) {
  const {
    data,
    favoriteData = [],
    lastSelectedRoom,
    isFavorite,
    onPageChange,
    onSelect,
    onTextSearch,
    onToggleFavorite,
    totalCount,
  } = props
  const { t } = useTranslation()

  const [page, setPage] = useState<number>(1)
  const [, setRowsPerPage] = useState(5)
  const [selectedRow, setSelectedRow] = useState<string>()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onTextSearch?.(event.target.value)
  }

  const handleChangePage = (newPage: number, rowsPerPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage, rowsPerPage)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    if (Math.ceil(totalCount / newRowsPerPage) >= page) onPageChange?.(page, newRowsPerPage)
  }

  const handleSelect = (room: WaitingRoomData) => {
    if (!room.isOpening) return
    setSelectedRow(room.id)
    onSelect?.(room)
  }

  // Deduplicate: don't show favorites or last-selected again in the paginated list
  const favoriteIds = new Set(favoriteData.map((f) => f.id))
  const pinnedIds = new Set([...favoriteIds])
  if (lastSelectedRoom) pinnedIds.add(lastSelectedRoom.id)

  // Last selected that is not already a favorite
  const showLastSelected = lastSelectedRoom && !favoriteIds.has(lastSelectedRoom.id)
  const hasPinnedSection = favoriteData.length > 0 || showLastSelected

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column-reverse', sm: 'row' }} alignItems="center" spacing={2} justifyContent="space-between" pl="5px" pr="5px">
        <Box flex={1}>
          <TextInput fullWidth onChange={handleChange} placeholder={t('page.createConsultation.step.waitingRoomSelection.searchInput.placeholder')} />
        </Box>
      </Stack>

      {/* Favorites & last selected — always visible, not paginated */}
      {hasPinnedSection && (
        <Stack spacing={1}>
          {favoriteData.length > 0 && (
            <>
              <Stack direction="row" alignItems="center" spacing={1} px="5px">
                <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('page.createConsultation.step.waitingRoomSelection.favorites', { defaultValue: 'Favorites' })}
                </Typography>
              </Stack>
              <TableContainer>
                <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 8px', padding: '0 5px' }}>
                  <TableBody>
                    {favoriteData.map((room) => (
                      <WaitingRoomRow
                        key={room.id}
                        room={room}
                        isSelected={selectedRow === room.id}
                        isFav={true}
                        onSelect={() => handleSelect(room)}
                        onToggleFavorite={() => onToggleFavorite?.(room.id)}
                        t={t}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {showLastSelected && (
            <>
              <Stack direction="row" alignItems="center" spacing={1} px="5px">
                <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('page.createConsultation.step.waitingRoomSelection.lastSelected', { defaultValue: 'Last selected' })}
                </Typography>
              </Stack>
              <TableContainer>
                <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 8px', padding: '0 5px' }}>
                  <TableBody>
                    <WaitingRoomRow
                      room={lastSelectedRoom}
                      isSelected={selectedRow === lastSelectedRoom.id}
                      isFav={false}
                      onSelect={() => handleSelect(lastSelectedRoom)}
                      onToggleFavorite={() => onToggleFavorite?.(lastSelectedRoom.id)}
                      t={t}
                    />
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Divider sx={{ my: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('page.createConsultation.step.waitingRoomSelection.allRooms', { defaultValue: 'All waiting rooms' })}
            </Typography>
          </Divider>
        </Stack>
      )}

      {/* Paginated list — excludes pinned rooms */}
      <Stack spacing={2} mb="32px">
        <TableContainer>
          <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 15px', padding: '0 5px' }}>
            <TableBody>
              {data
                .filter((room) => !pinnedIds.has(room.id))
                .map((room) => (
                  <WaitingRoomRow
                    key={room.id}
                    room={room}
                    isSelected={selectedRow === room.id}
                    isFav={isFavorite?.(room.id) ?? false}
                    onSelect={() => handleSelect(room)}
                    onToggleFavorite={() => onToggleFavorite?.(room.id)}
                    t={t}
                  />
                ))}
            </TableBody>
          </Table>
          <Stack direction="row" justifyContent="end">
            <Pagination
              color="primary"
              itemsPerPageOptions={[5, 10, 15]}
              shape="rounded"
              page={page}
              onPageChange={handleChangePage}
              onItemsPerPageChange={handleRowsPerPageChange}
              totalItems={totalCount}
            />
          </Stack>
        </TableContainer>
      </Stack>
    </Stack>
  )
}

export default SelectWaitingRoomStep
